// The finished match's results, kept per tab so a reload of the match URL lands
// on the results screen instead of trying to rejoin a match that is over. The
// case that needs it: signing in from the match-end modal via an OAuth
// provider, which leaves the page and returns to this same URL. Keyed by the
// URL (what OAuth returns to), cleared on a deliberate leave and when a new
// match starts here, and ignored once stale.

export interface MatchEndSnapshot {
  matchConfig: any;
  score: Record<string, number>;
  ratingUpdates: Record<string, { before: number; after: number; firstMatch?: boolean }> | null;
  // Achievement levels reached this match, per seat (achievement_update).
  unlocks?: Record<string, { id: string; level: number; eggs: number }[]> | null;
  stats: any;
  localIds: string[];
  // The match's id once its replay is stored (the rounds themselves are far too big
  // for session storage; a restore fetches them again by this).
  replayMatchId?: string | null;
  at: number;
}

const PREFIX = "achtungMatchEnd:";
const MAX_AGE_MS = 15 * 60 * 1000;

function key(): string {
  return PREFIX + window.location.pathname + window.location.search;
}

export function saveMatchEndSnapshot(s: Omit<MatchEndSnapshot, "at">): void {
  try {
    sessionStorage.setItem(key(), JSON.stringify({ ...s, at: Date.now() }));
  } catch { /* storage off */ }
}

export function loadMatchEndSnapshot(): MatchEndSnapshot | null {
  try {
    const raw = sessionStorage.getItem(key());
    if (!raw) return null;
    const s = JSON.parse(raw) as MatchEndSnapshot;
    if (!s || !s.matchConfig || typeof s.at !== "number" || Date.now() - s.at > MAX_AGE_MS) {
      sessionStorage.removeItem(key());
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function clearMatchEndSnapshot(): void {
  try { sessionStorage.removeItem(key()); } catch { /* storage off */ }
}
